<template>
  <div class="market-page">

    <section class="hero-banner" @mouseenter="pauseSlider" @mouseleave="resumeSlider">
      <div class="hero-slider">
        <div
          v-for="(slide, i) in heroSlides"
          :key="i"
          class="hero-slide"
          :class="{ active: currentSlide === i }"
          :style="{ backgroundImage: `url(${slide.image})` }"
        >
          <div class="hero-overlay"></div>
          <div class="hero-content">
            <h2 class="hero-title">{{ $t(slide.title) }}</h2>
            <p class="hero-desc">{{ $t(slide.desc) }}</p>
            <button v-if="slide.link" class="hero-cta" @click="navigateTo(slide.link)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14"/><path d="M12 5l7 7-7 7"/>
              </svg>
              <span>{{ $t(slide.btn) }}</span>
            </button>
          </div>
        </div>
      </div>
      <div class="slider-dots">
        <button
          v-for="(slide, i) in heroSlides"
          :key="i"
          class="slider-dot"
          :class="{ active: currentSlide === i }"
          @click="goToSlide(i)"
        />
      </div>
    </section>

    <div ref="toolbarEl" class="market-toolbar" :class="{ 'is-stuck': toolbarStuck, 'is-hidden': toolbarHidden }">
      <div class="toolbar-bar">
        <button class="toolbar-filter-toggle" @click="filterHidden = false; filterOpen = !filterOpen">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="20" y2="12"/><line x1="12" y1="18" x2="20" y2="18"/>
          </svg>
          <span>{{ $t('market.filter') }}</span>
          <span v-if="filterCount" class="filter-count-badge">{{ filterCount }}</span>
        </button>

      <div v-if="hasActiveFilters && !filterHidden" class="active-filters-bar">
          <div class="strip-container">
            <div class="filter-chips">
              <span v-if="activeFilter.category" class="chip">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                {{ activeCategoryName }}
                <button @click="activeFilter.category = null; applyFilters()" class="chip-remove">&times;</button>
              </span>
              <span v-for="d in activeFilter.deal" :key="d" class="chip">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                {{ $t('market.' + d) }}
                <button @click="activeFilter.deal = activeFilter.deal.filter(x => x !== d); applyFilters()" class="chip-remove">&times;</button>
              </span>
              <span v-if="searchQuery" class="chip">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                {{ searchQuery }}
                <button @click="searchQuery = ''; applyFilters()" class="chip-remove">&times;</button>
              </span>
              <span v-if="kindId" class="chip">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                {{ $t(filterName(kindId, kindFilters)) }}
                <button @click="kindId = ''; applyFilters()" class="chip-remove">&times;</button>
              </span>
              <span v-if="payId" class="chip">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                {{ $t(filterName(payId, payFilters)) }}
                <button @click="payId = ''; applyFilters()" class="chip-remove">&times;</button>
              </span>
              <span v-for="p in activeFilter.provinces" :key="p" class="chip">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                {{ p }}
                <button @click="activeFilter.provinces = activeFilter.provinces.filter(x => x !== p); applyFilters()" class="chip-remove">&times;</button>
              </span>
              <span v-if="zaraFilter" class="chip">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                {{ $t('market.zara') }}
                <button @click="zaraFilter = false; applyFilters()" class="chip-remove">&times;</button>
              </span>
              <span v-if="roostabadFilter" class="chip">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                {{ $t('market.roostabad') }}
                <button @click="roostabadFilter = false; applyFilters()" class="chip-remove">&times;</button>
              </span>
              <button class="chip-clear" @click="clearFilters">{{ $t('market.clear_all') }}</button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!filterOpen && !isMobile" class="toolbar-strips">
        <section class="category-strip">
          <div class="strip-container">
            <button
              v-for="cat in categories"
              :key="cat.id"
              class="cat-pill"
              :class="{ active: activeFilter.category === cat.id }"
              @click="setCategory(cat.id)"
            >
              <div class="cat-icon">
                <img :src="cat.icon" :alt="cat.name" />
              </div>
              <span class="cat-name">{{ $t(cat.label) }}</span>
            </button>
          </div>
        </section>

        <section class="kind-strip">
          <div class="strip-container">
              <button
                v-for="kt in kindFilters"
                :key="'k' + kt.id"
                class="kind-pill"
                :class="{ active: kindId === String(kt.id) }"
                @click="toggleKind(kt.id, kt.label)"
              >
                <span class="kind-name">{{ $t(kt.label) }}</span>
              </button>
              <span class="strip-divider"></span>
              <button
              v-for="pt in payFilters"
              :key="'p' + pt.id"
              class="kind-pill"
              :class="{ active: payId === String(pt.id) }"
              @click="togglePay(pt.id, pt.label)"
              >
              <span class="kind-name">{{ $t(pt.label) }}</span>
            </button>
            <span class="strip-divider"></span>
            <button
            v-for="pt in productTypes"
            :key="pt.value"
            class="kind-pill"
              :class="{ active: productType.value === pt.value }"
              @click="toggleProductType(pt.value, pt.label)"
              >
              <span class="kind-name">{{ $t(pt.label) }}</span>
            </button>
            <span class="strip-divider"></span>
            <button
              v-for="b in brandFilters"
              :key="b.id"
              class="kind-pill"
              :class="b.id === 'zara' ? { active: zaraFilter } : { active: roostabadFilter }"
              @click="toggleBrand(b.id)"
            >
              <span class="brand-name">{{ $t(b.label) }}</span>
            </button>
          </div>
        </section>

        <section class="ostan-strip">
          <div class="strip-container">
            <span class="ostan-label">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 20l-5.45-2.72A2 2 0 0 1 2 15.54V5.36a2 2 0 0 1 2.91-1.79L9 5.5l6-3 5.09 2.54A2 2 0 0 1 22 6.46v10.18a2 2 0 0 1-2.91 1.79L15 18l-6 2z"/>
                <circle cx="9" cy="9" r="2.5"/><path d="M15 18V9"/>
              </svg>
              {{ $t('market.province') }}
            </span>
            <button
              v-for="o in provinces"
              :key="o.id"
              class="ostan-pill"
              :class="{ active: activeFilter.provinces.includes(o.label) }"
              @click="toggleOstan(o.id)"
            >
              <svg class="ostan-marker" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
              </svg>
              <span class="ostan-name">{{ o.label }}</span>
            </button>
          </div>
        </section>
      </div>

    </div>

    <div v-if="filterOpen && isMobile && !filterHidden" class="filter-backdrop" @click="filterOpen = false"></div>
    <div
      v-if="filterOpen && isMobile && !filterHidden"
      class="filter-drawer"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('market.filters')"
    >
      <div class="drawer-header">
        <div class="drawer-title">
          <h4>{{ $t('market.filters') }}</h4>
          <span v-if="filterCount" class="drawer-count">{{ filterCount }}</span>
        </div>
        <button ref="filterCloseBtn" class="drawer-close" :aria-label="$t('market.close')" @click="filterOpen = false">&times;</button>
      </div>
      <div class="drawer-scroll">
        <div class="drawer-group">
          <span class="drawer-group-title">{{ $t('market.categories') }}</span>
          <div class="drawer-options drawer-options-cats">
            <button
              v-for="cat in categories"
              :key="'dc' + cat.id"
              class="drawer-option drawer-option-card"
              :class="{ active: activeFilter.category === cat.id }"
              @click="setCategory(cat.id)"
            >
              <img :src="cat.icon" :alt="cat.name" />
              <span>{{ $t(cat.label) }}</span>
            </button>
          </div>
        </div>

        <div class="drawer-group">
          <span class="drawer-group-title">{{ $t('market.deal_type') }}</span>
          <div class="drawer-options">
            <button
              v-for="kt in kindFilters"
              :key="'dk' + kt.id"
              class="drawer-option"
              :class="{ active: kindId === String(kt.id) }"
              @click="toggleKind(kt.id, kt.label)"
            >
              <span>{{ $t(kt.label) }}</span>
            </button>
            <button
              v-for="pt in payFilters"
              :key="'dp' + pt.id"
              class="drawer-option"
              :class="{ active: payId === String(pt.id) }"
              @click="togglePay(pt.id, pt.label)"
            >
              <span>{{ $t(pt.label) }}</span>
            </button>
          </div>
        </div>

        <div class="drawer-group">
          <span class="drawer-group-title">{{ $t('market.province') }}</span>
          <div class="drawer-options drawer-options-ostan">
            <button
              v-for="o in provinces"
              :key="'do' + o.id"
              class="drawer-option"
              :class="{ active: activeFilter.provinces.includes(o.label) }"
              @click="toggleOstan(o.id)"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
              </svg>
              <span>{{ o.label }}</span>
            </button>
          </div>
        </div>

        <div class="drawer-group">
          <span class="drawer-group-title">{{ $t('market.brand_filters') }}</span>
          <div class="drawer-options">
            <button
              v-for="b in brandFilters"
              :key="'brand' + b.id"
              class="drawer-option"
              :class="b.id === 'zara' ? { active: zaraFilter } : { active: roostabadFilter }"
              @click="toggleBrand(b.id)"
            >
              <span>{{ $t(b.label) }}</span>
            </button>
          </div>
        </div>
      </div>
      <div class="drawer-footer">
        <button class="drawer-clear" @click="clearFilters">{{ $t('market.clear_all') }}</button>
        <button class="drawer-apply" @click="filterOpen = false">
          {{ $t('market.show_results') }} ({{ products.length }})
        </button>
      </div>
    </div>

    <section class="products-section">
      <div class="section-container">
        <div class="section-header reveal">
          <div class="header-left">
            <h3 class="section-title">{{ $t('market.products') }}</h3>
            <span class="product-count">{{ totalProducts}} {{ $t('market.product_unit') }}</span>
          </div>
          <div class="header-right">
            <div class="header-search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
              </svg>
              <input
                v-model="searchQuery"
                type="search"
                :placeholder="$t('market.search_placeholder')"
                @keyup.enter="applyFilters"
              />
              <button v-if="searchQuery" class="search-clear" :aria-label="$t('market.search_clear')" @click="searchQuery = ''; applyFilters()">&times;</button>
            </div>
            <div class="grid-toggle-group">
              <button
                v-for="n in gridOptions"
                :key="n"
                class="grid-toggle-btn"
                :class="{ active: gridCols === n }"
                @click="gridCols = n; updateUrl()"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect v-for="i in n" :key="i" :x="((i - 1) % 3) * 8 + 1" :y="(Math.floor((i - 1) / 3)) * 8 + 1" width="6" height="6" rx="1"/>
                </svg>
              </button>
            </div>
            <div class="sort-wrapper">
              <svg class="sort-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M11 5h10"/><path d="M11 9h7"/><path d="M11 13h4"/>
              </svg>
              <select v-model="sortBy" @change="applyFilters" class="sort-select">
                <option value="newest">{{ $t('market.newest') }}</option>
                <option value="price_asc">{{ $t('market.price_asc') }}</option>
                <option value="price_desc">{{ $t('market.price_desc') }}</option>
              </select>
            </div>
          </div>
        </div>

        <div v-if="loading && !page" class="loading-grid">
          <div v-for="n in 6" :key="n" class="sk-card">
            <div class="sk-img"></div>
            <div class="sk-body">
              <div class="sk-line"></div>
              <div class="sk-line short"></div>
              <div class="sk-line tiny"></div>
            </div>
          </div>
        </div>

        <div v-else-if="!products.length && !loading" class="empty-state">
          <div class="empty-orb">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
            </svg>
          </div>
          <h4>{{ $t('market.no_products') }}</h4>
          <p>{{ $t('market.no_products_desc') }}</p>
          <button class="reset-btn" @click="clearFilters">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
            {{ $t('market.clear_filters') }}
          </button>
        </div>

        <div v-else ref="productsGrid" class="products-grid" :style="{ '--grid-cols': gridCols }">
          <template v-for="(item, i) in displayItems" :key="item._isCta ? item._ctaId : (item._id || item.id)">
            <div
              v-if="!item._isCta"
              class="product-card"
            >
              <div class="card-inner">
                <div v-if="hasBadges(item)" class="badge-lists">
                  <span v-if="item.instant" class="badge badge-success">{{ $t('market.instant') }}</span>
                  <span v-if="item.special" class="badge badge-warning">{{ $t('market.special') }}</span>
                  <span v-if="item.hot" class="badge badge-danger">{{ $t('market.hot') }}</span>
                  <span v-if="item.zara" class="badge badge-gold">{{ $t('market.zara') }}</span>
                  <span v-if="item.roostabad" class="badge badge-blue">{{ $t('market.roostabad') }}</span>
                  <span v-if="item.adType == 'outofseasion'" class="badge badge-danger">{{ $t('market.type_presell') }}</span>
                  <span v-if="isFreshProduct(item)" class="badge badge-fresh">{{ $t('market.fresh') }}</span>
                </div>
                <NuxtLink :to="localePath(`/details/${item.invoiceNum || item._id}`)" class="product-thumbnail"  target="_blank" >
                  <NuxtImg
                    :src="productImage(item)"
                    :alt="item.title"
                    loading="lazy"
                    @error.once="onImgError($event)"
                  />
                </NuxtLink>
                <NuxtLink :to="localePath(`/details/${item.invoiceNum || item._id}`)" class="product-title"  target="_blank" >
                  {{ item.title }}
                </NuxtLink>
                <p class="seller-info">
                  {{ item.username }}{{ item.address?.city ? ' | ' + item.address.city : '' }}
                </p>
                <NuxtLink class="phone-btn" :to="localePath(`/details/${item._id}`)"  target="_blank" >
                  <i class="fa-solid fa-phone"></i>
                </NuxtLink>
              </div>
            </div>

            <div
              v-else
              class="cta-card"
              :class="'cta-style-' + ((item._ctaId % 2) + 1)"
            >
              <div class="cta-atmosphere">
                <svg class="cta-leaf" viewBox="0 0 60 60">
                  <path fill="currentColor" d="M30 5C18 12 10 24 10 38c0 7 4 13 10 17 0-12 4-23 10-30 6 7 10 18 10 30 6-4 10-10 10-17 0-14-8-26-20-33z"/>
                  <path fill="currentColor" opacity="0.3" d="M30 25v30"/>
                </svg>
              </div>
              <div class="cta-body">
                <h4 class="cta-label">{{ $t(item.cta.title) }}</h4>
                <p class="cta-desc">{{ $t(item.cta.text) }}</p>
                <button class="cta-action" @click="navigateTo(item.cta.action,target='_blank')">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M5 12h14"/><path d="M12 5l7 7-7 7"/>
                  </svg>
                  <span>{{ $t(item.cta.button) }}</span>
                </button>
              </div>
            </div>
          </template>
        </div>

        <div ref="scrollSentinel" class="scroll-sentinel reveal">
          <div v-if="loading && page" class="sentinel-inner">
            <div class="sentinel-spinner">
              <svg viewBox="0 0 50 50">
                <circle cx="25" cy="25" r="20" fill="none" stroke-width="4" stroke-linecap="round"/>
              </svg>
            </div>
            <span>{{ $t('market.loading_more') }}</span>
          </div>
          <div v-else-if="hasMore" class="sentinel-inner">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 13l5 5 5-5"/><path d="M7 6l5 5 5-5"/></svg>
            <span>{{ $t('market.scroll_for_more') }}</span>
          </div>
          <div v-else class="sentinel-inner sentinel-end">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            <span>{{ $t('market.all_loaded') }}</span>
          </div>
        </div>
      </div>
    </section>

    <div v-if="tags" class="tags-seo-container">
        <div v-if="tags" v-html="tags" class="seo-content"></div>
        <div v-else class="seo-loading">
          <div class="loading-spinner"></div>
          <p>{{ $t('market.seo_loading') }}</p>
        </div>
    </div>

  </div>
</template>

<script setup>
const baseURL = useRuntimeConfig().app.baseURL
const siteUrl = useRuntimeConfig().public.baseUrl
const { t, locale } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()

const products = ref([])
const totalProducts = ref(0)
const tags = ref('')
const page = ref(1)
const loading = ref(false)
const hasMore = ref(true)
const sortBy = ref('newest')
const perPage = ref(12)
const filterOpen = ref(false)
const toolbarStuck = ref(false)
const filterHidden = ref(false)
let lastScrollY = 0
const searchQuery = ref('')
const kindId = ref('')
const payId = ref('')
const productType = ref('')
const zaraFilter = ref(false)
const roostabadFilter = ref(false)
const gridCols = ref(4)
const isMobile = ref(false)
const gridOptions = computed(() => isMobile.value ? [1, 2, 3] : [4, 5, 6])

// const { data: tagResponse } = await useFetch('/market/api/tags_txt', {
//   query: {
//     catId: () =>  route.params.catId || ''
//   },
//   default: () => ({ content: '' }),
//   watch: [() =>  route.params.catId]
// })

// const tags = computed(() => tagResponse.value?.content || '')

const checkViewport = () => {
  const mobile = window.innerWidth < 768
  if (mobile !== isMobile.value) {
    isMobile.value = mobile
    if (mobile) gridCols.value = 2
  }
}
if (process.client) {
  checkViewport()
  window.addEventListener('resize', checkViewport)
  onBeforeUnmount(() => window.removeEventListener('resize', checkViewport))
}

const kindFilters = [
  { id: 3358, label: 'market.filter_export' },
  { id: 2851, label: 'market.cat_grocery' },
  { id: 517, label: 'market.kind_factory' },
]
const payFilters = [
  { id: 3363, label: 'market.deal_commission' },
  { id: 3364, label: 'market.deal_cash' },
]

const brandFilters = [
  { id: 'roostabad', label: 'market.roostabad' },
  { id: 'zara', label: 'market.zara' },
]

const scrollSentinel = ref(null)
const productsGrid = ref(null)
const toolbarEl = ref(null)
const filterCloseBtn = ref(null)

const currentSlide = ref(0)
const sliderPaused = ref(false)
let sliderTimer = null
let toolbarScrollListener = null

const activeFilter = reactive({
  type: 'seller',
  category: null,
  deal: [],
  provinces: []
})

function formatText(html) {
  if (!html) return ''
  const temp = document.createElement('div')
  temp.innerHTML = html.replace(/\u200E/g, '')
  return temp.textContent || temp.innerText || ''
}
const ctaTexts = [
  { title: 'market.cta_title_1', text: 'market.cta_text_1', button: 'market.cta_button_1', action: '/add-ord'  },
  { title: 'market.cta_title_2', text: 'market.cta_text_2', button: 'market.cta_button_2', action: '/add-adv'  },
  { title: 'market.cta_title_3', text: 'market.cta_text_3', button: 'market.cta_button_3', action: '/add-ord' },
  { title: 'market.cta_title_4', text: 'market.cta_text_4', button: 'market.cta_button_4', action: '/add-ord' },
  { title: 'market.cta_title_5', text: 'market.cta_text_5', button: 'market.cta_button_5', action: '/add-adv' },
  { title: 'market.cta_title_6', text: 'market.cta_text_6', button: 'market.cta_button_6', action: '/about-services' },
  { title: 'market.cta_title_7', text: 'market.cta_text_7', button: 'market.cta_button_7', action: '/about-services' },
  { title: 'market.cta_title_8', text: 'market.cta_text_8', button: 'market.cta_button_8', action: '/add-ord' },
  { title: 'market.cta_title_9', text: 'market.cta_text_9', button: 'market.cta_button_9', action: '/add-ord' },
  { title: 'market.cta_title_10', text: 'market.cta_text_10', button: 'market.cta_button_10', action: '/products/outofseasion' }
]


const displayItems = computed(() => {
  const result = []
  const ps = perPage.value || 12
  let ctaIdx = 0

  for (let i = 0; i < products.value.length; i++) {
    result.push(products.value[i])

    if ((i + 1) % ps === 0 && (i + 1) < products.value.length) {
      result.push({
        _isCta: true,
        cta: ctaTexts[ctaIdx % ctaTexts.length],
        _ctaId: ctaIdx
      })
      ctaIdx++
    }
  }
  return result
})

const heroSlides = computed(() =>
  isRoostabad.value
    ? [
        { image: `${baseURL}img/slider/roostaabad.webp`, title: "", desc: "", link: '/add-adv', btn: 'market.hero_1_btn' },
        { image: `${baseURL}img/slider/roostaabad_2.webp`, title: "market.hero_rootsa_title", desc: "", link: '/add-adv', btn: 'market.hero_1_btn' },
        { image: `${baseURL}img/slider/roostaabad_2.webp`, title: "market.hero_rootsa_title_2", desc: "", link: '/add-adv', btn: 'market.hero_1_btn' },
      ]
    : [
        { image: `${baseURL}img/slider/slider1.webp`, title: 'market.hero_1_title', desc: 'market.hero_1_desc', link: '/add-adv', btn: 'market.hero_1_btn' },
        { image: `${baseURL}img/slider/slider2.webp`, title: 'market.hero_2_title', desc: 'market.hero_2_desc', link: '/add-ord', btn: 'market.hero_2_btn' },
        { image: `${baseURL}img/slider/slider3.webp`, title: 'market.hero_3_title', desc: 'market.hero_3_desc', link: '/about-sarvban', btn: 'market.hero_3_btn' },
      ]
)

const openHotGroup = ref(null)
function toggleHotGroup(i) {
  openHotGroup.value = openHotGroup.value === i ? null : i
}

const hotGroups = [
  {
    icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>',
    label: 'market.hot_group_export',
    items: [
      { href: '/market/cat/1749/سیب?kind=3358', label: 'market.hot_export_apple' },
      { href: '/market/cat/1591/گل%20کلم?kind=3358', label: 'market.hot_export_cauliflower' },
      { href: '/market/cat/2207/فلفل%20دلمه%20ای?kind=3358', label: 'market.hot_export_pepper' },
      { href: '/market/cat/1588/گوجه%20فرنگی?kind=3358', label: 'market.hot_export_tomato' },
      { href: '/market/cat/1596/هویج?kind=3358', label: 'market.hot_export_carrot' },
      { href: '/market/cat/1567/کاهو?kind=3358', label: 'market.hot_export_lettuce' },
      { href: '/market/cat/1583/هندوانه?kind=3358', label: 'market.hot_export_watermelon' },
      { href: '/market/cat/1593/پیاز?kind=3358', label: 'market.hot_export_onion' },
    ],
    allHref: '/market/products/seller',
    allLabel: 'market.hot_all_export',
  },
  {
    icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>',
    label: 'market.hot_group_super',
    items: [
      { href: '/market/cat/2207/فلفل%20دلمه%20ای?kind=2851', label: 'market.hot_super_pepper' },
      { href: '/market/cat/1584/خیار?kind=2851', label: 'market.hot_super_cucumber' },
      { href: '/market/cat/1757/پرتقال?kind=2851', label: 'market.hot_super_orange' },
      { href: '/market/cat/1593/پیاز?kind=2851', label: 'market.hot_super_onion' },
    ],
    allHref: '/market/products/seller',
    allLabel: 'market.hot_all_super',
  },
  {
    icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 20h20"/><path d="M5 20V8l7-4 7 4v12"/><path d="M9 20v-6h6v6"/></svg>',
    label: 'market.hot_group_factory',
    items: [
      { href: '/market/cat/1765/خرما?kind=2852', label: 'market.hot_factory_date' },
      { href: '/market/cat/4740/پیاز%20خشک?kind=2852', label: 'market.hot_factory_dried_onion' },
      { href: '/market/cat/2082/لوبیا%20قرمز?kind=2852', label: 'market.hot_factory_red_bean' },
    ],
    allHref: '/market/products/service',
    allLabel: 'market.hot_all_factory',
  },
  {
    icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
    label: 'market.hot_group_providers',
    items: [
      { href: '/market/cat/service/48131/بارفروش و حق العمل کاران', label: 'market.hot_provider_broker' },
      { href: '/market/cat/48142/تولید%20سبد%20پلاستیکی', label: 'market.hot_provider_basket' },
      { href: '/market/cat/service/48144/تولید%20کیسه%20بسته%20بندی', label: 'market.hot_provider_bag' },
    ],
    allHref: '/market/products/service',
    allLabel: 'market.hot_all_providers',
  },
]

function startSlider() {
  stopSlider()
  sliderTimer = setInterval(() => {
    if (!sliderPaused.value) {
      currentSlide.value = (currentSlide.value + 1) % heroSlides.length
    }
  }, 5000)
}

function stopSlider() {
  if (sliderTimer) {
    clearInterval(sliderTimer)
    sliderTimer = null
  }
}

function pauseSlider() { sliderPaused.value = true }
function resumeSlider() { sliderPaused.value = false }
function goToSlide(i) { currentSlide.value = i }

const categories = [
  { id: 77, icon: `${baseURL}img/icons/baghi.svg`, name: 'باغی', label: 'market.cat_orchard' },
  { id: 37, icon: `${baseURL}img/icons/zeraei.svg`, name: 'زراعی', label: 'market.cat_farming' },
  { id: 568, icon: `${baseURL}img/icons/daru.svg`, name: 'گیاهان دارویی', label: 'productCategories.herbal' },
  { id: 1878, icon: `${baseURL}img/icons/fish.webp`, name: 'ماهی و ابزیان', label: 'productCategories.fish' },
  { id: 4170, icon: `${baseURL}img/icons/houseplants.png`, name: 'گیاهان تزینی', label: 'productCategories.plants' },
{ id: 517, icon: `${baseURL}img/icons/ghaza.svg`, name: 'صنایع غذایی', label: 'market.cat_food_ind' },
  { id: 457, icon: `${baseURL}img/icons/nahadeh.svg`, name: 'نهاده ها', label: 'market.cat_inputs' },
  { id: 4813, icon: `${baseURL}img/icons/service.png`, name: 'خدمات دهندگان بازار', label: 'market.service_providers' },
  // { id: 2282, icon: `${baseURL}img/icons/kiwi.svg`, name: 'کیوی', label: 'market.cat_kiwi' },
  // { id: 3358, icon: '/img/icons/saderat.svg', name: 'صادراتی', label: 'market.cat_export' },
  // { id: 2851, icon: '/img/icons/tarebar.svg', name: 'سوپری', label: 'market.cat_grocery' },
  // { id: 3363, icon: '/img/icons/amal.svg', name: 'حق العملی', label: 'market.cat_commission' },
]

const productTypes = [
  { value: 'seller', label: 'market.type_seller' },
  // { value: 'buyer', label: 'market.type_buyer' },
  { value: 'outofseasion', label: 'market.filter_presell' },
]
const filterCategories = [
  ...categories,
  { id: 'export', icon: 'fa-solid fa-ship', label: 'market.filter_export' },
  { id: 'presell', icon: 'fa-solid fa-calendar', label: 'market.filter_presell' },
]


const dealTypes = [
  { value: 'cash', label: 'market.deal_cash' },
  { value: 'commission', label: 'market.deal_commission' },
]

const provinces = [
   { "ostan_id": 3, "label": "آذربایجان شرقی", "id": 31, "latlng": "37.55671,45.06484" }, 
{ "ostan_id": 4, "label": "آذربایجان غربی", "id": 30, "latlng": "38.0851,46.28718" },
{ "ostan_id": 24, "label": "اردبیل", "id": 2, "latlng": "38.24302,48.29575" },
{ "ostan_id": 10, "label": "اصفهان", "id": 5, "latlng": "32.65794,51.71742" }, 
{ "ostan_id": 30, "label": "البرز", "id": 1, "latlng": "35.82684,50.94775" },
{ "ostan_id": 16, "label": "ایلام", "id": 11, "latlng": "33.63328,46.39966" }, 
{ "ostan_id": 18, "label": "بوشهر", "id": 3, "latlng": "28.92432,50.85397" }, 
{ "ostan_id": 23, "label": "تهران", "id": 27, "latlng": "35.6801,51.3606" },
{ "ostan_id": 40, "label": "جنوب کرمان", "id": 32, "latlng": "30.28281,57.05667" },
{ "ostan_id": 29, "label": "خراسان جنوبی", "id": 14, "latlng": "32.87066,59.21839" },
{ "ostan_id": 9, "label": "خراسان رضوی", "id": 15, "latlng": "36.29969,59.5872" },
{ "ostan_id": 28, "label": "خراسان شمالی", "id": 16, "latlng": "37.47564,57.32559" }, 
{ "ostan_id": 6, "label": "خوزستان", "id": 17, "latlng": "31.31864,48.67337" }, 
{ "ostan_id": 19, "label": "زنجان", "id": 29, "latlng": "36.67737,48.50626" },
{ "ostan_id": 20, "label": "سمنان", "id": 25, "latlng": "35.57941,53.38607" }, 
{ "ostan_id": 11, "label": "سیستان و بلوچستان", "id": 26, "latlng": "29.4874,60.85841" },
{ "ostan_id": 7, "label": "فارس", "id": 6, "latlng": "29.64473,52.52903" }, 
{ "ostan_id": 26, "label": "قزوین", "id": 23, "latlng": "36.28164,50.01802" },
{ "ostan_id": 25, "label": "قم", "id": 24, "latlng": "34.65128,50.85422" }, 
{ "ostan_id": 15, "label": "لرستان", "id": 20, "latlng": "33.4673,48.32658" }, 
{ "ostan_id": 2, "label": "مازندران", "id": 22, "latlng": "36.55048,53.06165" },
{ "ostan_id": 0, "label": "مرکزی", "id": 21, "latlng": "34.09579,49.73263" }, 
{ "ostan_id": 22, "label": "هرمزگان", "id": 10, "latlng": "27.194,56.27654" },
{ "ostan_id": 13, "label": "همدان", "id": 9, "latlng": "34.80212,48.51682" },
{ "ostan_id": 14, "label": "چهارمحال و بختیاری", "id": 4, "latlng": "32.33064,50.86313" }, 
{ "ostan_id": 12, "label": "کردستان", "id": 19, "latlng": "35.29063,47.01913" },
{ "ostan_id": 8, "label": "کرمان", "id": 12, "latlng": "30.28281,57.05667" }, 
{ "ostan_id": 5, "label": "کرمانشاه", "id": 13, "latlng": "34.36466,47.13182" },
{ "ostan_id": 17, "label": "کهگیلویه وبویراحمد", "id": 18, "latlng": "30.67549,51.57134" },
{ "ostan_id": 27, "label": "گلستان", "id": 8, "latlng": "36.83508,54.42563" },
{ "ostan_id": 1, "label": "گیلان", "id": 7, "latlng": "37.27786,49.58741" },
{ "ostan_id": 21, "label": "یزد", "id": 28, "latlng": "31.89154,54.34473" } ]
const ostanDataMap = ref({})


const hasActiveFilters = computed(() =>
  activeFilter.category || activeFilter.deal.length || activeFilter.provinces.length || searchQuery.value || kindId.value || payId.value || zaraFilter.value || roostabadFilter.value
)

const filterCount = computed(() =>
  (activeFilter.category ? 1 : 0) +
  activeFilter.deal.length +
  activeFilter.provinces.length +
  (searchQuery.value ? 1 : 0) +
  (kindId.value ? 1 : 0) +
  (payId.value ? 1 : 0) +
  (zaraFilter.value ? 1 : 0) +
  (roostabadFilter.value ? 1 : 0)
)

function filterName(id, list) {
  const found = list.find(f => String(f.id) === String(id))
  return found ? found.label : id
}

const routeName = computed(() => String(route.name).replace(/___\w+$/, ''))

// Auto-enable the zara / roostabad filter when the URL lands on their product page
// or carries the corresponding query param (?zara=true / ?roostabad=true).
const isZara = computed(() => route.path.includes('/product/zara') || route.query.zara === 'true' || route.query.zara === true)
const isRoostabad = computed(() => route.path.includes('/product/rostabad') || route.path.includes('/product/roostabad') || route.query.roostabad === 'true' || route.query.roostabad === true)

const searchQueryText = computed(() => {
  const fromParams = (route.params.key || '').trim()
  const fromQuery = (route.query.q || route.query.key || route.query.search || searchQuery.value || '').trim()
  return fromParams || fromQuery || ''
})

const pageTitle = computed(() => {
  const parts = []
  if (searchQueryText.value) {
    parts.push(searchQueryText.value)
  }
  if (activeFilter.category) {
    parts.push(activeCategoryName.value)
  }
  if (kindId.value) {
    parts.push(t(filterName(kindId.value, kindFilters)))
  }
  if (payId.value) {
    parts.push(t(filterName(payId.value, payFilters)))
  }
  if (!parts.length) {
    parts.push(t('market.title'))
  }
  return parts.join(' | ')
})

const pageDescription = computed(() => {
  const parts = []
  if (searchQueryText.value) {
    parts.push(searchQueryText.value)
  }
  if (activeFilter.category) {
    parts.push(activeCategoryName.value)
  }
  if (kindId.value) {
    parts.push(t(filterName(kindId.value, kindFilters)))
  }
  if (payId.value) {
    parts.push(t(filterName(payId.value, payFilters)))
  }
  const text = parts.join(' - ')
  return text || t('market.description')
})

const activeCategoryName = computed(() => {
  const cat = categories.find(c => c.id === activeFilter.category)
  if (cat) {
    return t(cat.label)
  }
  // Fall back to catTitle from URL params when category not in local categories array
  // This handles URLs like /cat/1565/سبزیجات%20ریشه%20ای%20و%20غده%20ای
  // where the category ID may not be in the hardcoded categories list
  return route.params.catTitle || ""
})

const canonicalUrl = computed(() => {
  const localePrefix = locale.value === 'fa' ? '' : locale.value + '/'
  const path = route.fullPath.replace(/^\/market/, '').replace(/^\//, '')
  if (!path) return `${siteUrl}${baseURL}${localePrefix}`
  return `${siteUrl}${baseURL}${localePrefix}${path}`
})

useSeoMeta({
  title: () => pageTitle.value,
  description: () => pageDescription.value,
  ogTitle: () => pageTitle.value,
  ogDescription: () => pageDescription.value,
  ogType: 'website',
  ogImage: () => `${siteUrl}${baseURL}img/slider/main-page.jpg`,
  ogUrl: () => canonicalUrl.value,
  ogSiteName: 'سروبان',
  twitterCard: 'summary_large_image',
  twitterTitle: () => pageTitle.value,
  twitterDescription: () => pageDescription.value,
  twitterImage: () => `${siteUrl}${baseURL}img/slider/main-page.jpg`
})

useHead({
  link: [
    { rel: 'canonical', href: canonicalUrl }
  ],
  htmlAttrs: {
    lang: locale.value === 'ar' ? 'ar' : locale.value === 'ru' ? 'ru' : locale.value === 'tr' ? 'tr' : locale.value === 'en' ? 'en' : 'fa',
    dir: locale.value === 'en' || locale.value === 'ru' || locale.value === 'tr' ? 'ltr' : 'rtl'
  }
})

const marketSchema = computed(() => {
  const nodes = [
    defineWebPage({
      name: pageTitle.value,
      description: pageDescription.value,
      url: canonicalUrl.value,
      inLanguage: { fa: 'fa-IR', en: 'en-IR', ar: 'ar-IR', ru: 'ru-RU', tr: 'tr-TR' }[locale.value] || 'fa-IR'
    })
  ]

  const breadcrumbItems = []
  breadcrumbItems.push({ name: 'سروبان', item: `${siteUrl}${baseURL}` })

  if (activeFilter.category && activeCategoryName.value) {
    breadcrumbItems.push({
      name: activeCategoryName.value,
      item: `${siteUrl}${baseURL}${locale.value === 'fa' ? '' : locale.value + '/'}cat/${activeFilter.category}/${encodeURIComponent(activeCategoryName.value)}`
    })
  }

  if (routeName.value === 'market-search' || routeName.value === 'market-search-simple') {
    const q = (route.params.key || searchQuery.value || '').trim()
    if (q) {
      breadcrumbItems.push({
        name: t('market.search_placeholder').replace('...', '') + ' ' + q,
        item: canonicalUrl.value
      })
    }
  }

  if (breadcrumbItems.length > 1 || routeName.value === 'market-search' || routeName.value === 'market-search-simple') {
    nodes.push(
      defineBreadcrumb({
        itemListElement: breadcrumbItems
      })
    )
  }

  const visibleProducts = products.value.slice(0, 12)
  if (visibleProducts.length) {
    nodes.push(
      defineItemList({
        itemListElement: visibleProducts.map((p, i) =>
          defineListItem({
            position: i + 1,
            name: p.title || '',
            item: p.invoiceNum ? `${baseURL}${localePath(`/details/${p.invoiceNum}`)}` : undefined
          })
        )
      })
    )
  }

  return nodes
})

useSchemaOrg(marketSchema)

function provinceId(label) {
  return provinces.find(p => p.label === label)?.id || ''
}

function isFreshProduct(product) {
  const raw = product.updatedAt_z || product.updatedAt || product.updated_at || ''
  if (!raw) return false
  let time
  if (typeof raw === 'number') {
    time = raw < 1e12 ? raw * 1000 : raw
  } else {
    time = new Date(raw).getTime()
  }
  if (!time || Number.isNaN(time)) return false
  const diff = Date.now() - time
  return diff >= 0 && diff < 3 * 24 * 60 * 60 * 1000
}

function hasBadges(item) {
  return item.instant || item.special || item.hot || item.zara || item.adType === 'outofseasion' || isFreshProduct(item)
}

function productImage(product) {
  if (product.image?.[0]) {
    const img = product.image[0]
    if (img.includes('.mp4')) return `${siteUrl}/img/icons/default-video.jpg`
    if (img.startsWith('/')) return img
    return `https://sarvban.com/uploads/market/${product.userId}/${img}`
  }
  if (typeof product.image === 'string') return product.image
  return `${baseURL}img/products/default.jpg`
}

function onImgError(e) {
  if (e.target) {
    e.target.src = `${baseURL}img/products/default.jpg`
  }
}

function formatPrice(price) {
  if (!price) return '—'
  return new Intl.NumberFormat(locale.value === 'fa' ? 'fa-IR' : 'en-US').format(price) + ' ' + t('market.currency')
}

function setCategory(id) {
  if (id === 4813 || id === 457) {
    navigateTo(localePath('/services'))
    return
  }
  activeFilter.category = activeFilter.category === id ? null : id
  applyFilters()
}

function toggleFilter(type, value) {
  if (type === 'category') {
    activeFilter.category = activeFilter.category === value ? null : value
  }
  applyFilters()
}

function toggleKind(id, label) {
  kindId.value = kindId.value === String(id) ? '' : String(id)
  applyFilters()
}

function togglePay(id, label) {
  payId.value = payId.value === String(id) ? '' : String(id)
  applyFilters()
}

function toggleProductType(value, label) {
  productType.value = productType.value === value ? '' : value
  applyFilters()
}

function syncBrandFromRoute() {
  zaraFilter.value = isZara.value
  roostabadFilter.value = isRoostabad.value
}

function toggleBrand(id) {
  if (id === 'zara') zaraFilter.value = !zaraFilter.value
  else roostabadFilter.value = !roostabadFilter.value
  applyFilters()
}

function toggleOstan(id) {
  const o = provinces.find(p => p.id === Number(id))
  if (!o) return
  const idx = activeFilter.provinces.indexOf(o.label)
  if (idx > -1) {
    activeFilter.provinces.splice(idx, 1)
  } else {
    activeFilter.provinces.push(o.label)
  }
  applyFilters()
}

function clearFilters() {
  activeFilter.category = null
  activeFilter.deal = []
  activeFilter.provinces = []
  searchQuery.value = ''
  kindId.value = ''
  payId.value = ''
  zaraFilter.value = false
  roostabadFilter.value = false
  filterOpen.value = false
  applyFilters()
  router.replace({ path: localePath('/products'), query: {} })
}

function applyFilters() {
  page.value = 1
  products.value = []
  hasMore.value = true
  fetchProducts()
  updateUrl()
}

function getCategoryName(id) {
  const cat = categories.find(c => c.id === id)
  return cat ? cat.name : ''
}

function getKindName(id) {
  const kind = kindFilters.find(k => String(k.id) === String(id))
  return kind ? kind.label : id
}

function updateUrl() {
  const query = {}
  const orderMap = { newest: '1', price_asc: '3', price_desc: '2' }
  const order = orderMap[sortBy.value] || '1'
  if (order !== '1') query.order = order

  const dealLabels = activeFilter.deal.map(d => t(`market.${d}`)).filter(Boolean)
  if (dealLabels.length) {
    query.pay_txt = dealLabels.join('،')
  }

  if (activeFilter.provinces.length) {
    query.province = activeFilter.provinces.map(provinceId).filter(Boolean).join(',')
    query.ost_txt = activeFilter.provinces.join('،')
  }

  const activeSearch = searchQuery.value
  if (kindId.value) query.kind = kindId.value
  if (payId.value) query.pay = payId.value
  if (gridCols.value !== 4) query.cols = String(gridCols.value)
  if (zaraFilter.value) query.zara = 'true'
  if (roostabadFilter.value) query.roostabad = 'true'

  const basePath = activeSearch
    ? `/search/${encodeURIComponent(activeSearch)}`
    : kindId.value
      ? `/kind/${activeFilter.type}/${kindId.value}/${encodeURIComponent(getKindName(kindId.value))}`
      : activeFilter.category
        ? `/cat/${activeFilter.category}/${encodeURIComponent(getCategoryName(activeFilter.category))}`
        : productType.value
          ? `/products/${productType.value}`
          : '/products'

  router.replace({
    path: localePath(basePath),
    query,
  })
}

function syncFiltersFromUrl() {
  const q = route.query
  const p = route.params
  const routeName = String(route.name).replace(/___\w+$/, '')

  if (routeName === 'products-outofseasion') {
    activeFilter.type = 'outofseasion'
  } else if (routeName === 'products-seller') {
    activeFilter.type = 'seller'
  } else {
    activeFilter.type = p.advType || 'seller'
  }
  searchQuery.value = p.key || ''

  if (routeName === 'market-cat' && p.catId) {
    activeFilter.category = Number(p.catId)
  } else if (routeName === 'market-cat-simple' && p.catId) {
    activeFilter.category = Number(p.catId)
    activeFilter.type = 'seller'
  } else if (routeName === 'cat-category' && p.catId) {
    activeFilter.category = Number(p.catId)
    activeFilter.type = 'seller'
  } else if (p.catId) {
    activeFilter.category = Number(p.catId)
  }

  if (routeName === 'market-kind' && p.kindId) {
    kindId.value = p.kindId
    payId.value = ''
    const kindMap = { 'نقدی': 'cash', 'حق العملی': 'commission' }
    if (p.kindTitle && kindMap[p.kindTitle]) {
      activeFilter.deal = [kindMap[p.kindTitle]]
    }
  }

  if (q.pay_txt) {
    const labels = q.pay_txt.split('،')
    const map = { 'نقدی': 'cash', 'حق العملی': 'commission' }
    const matched = labels.map(l => map[l]).filter(Boolean)
    if (matched.length) {
      activeFilter.deal = matched
    }
  }
  if (q.ost_txt) {
    activeFilter.provinces = q.ost_txt.split('،').map(s => s.trim()).filter(Boolean)
  } else if (q.ostan) {
    activeFilter.provinces = q.ostan.split('،').map(s => s.trim()).filter(Boolean)
  } else if (q.province) {
    activeFilter.provinces = q.province
      .split(',')
      .map(id => provinces.find(p => p.id === Number(id))?.label)
      .filter(Boolean)
  }
  if (q.order) {
    const orderMap = { '1': 'newest', '3': 'price_asc', '2': 'price_desc' }
    sortBy.value = orderMap[q.order] || 'newest'
  }
  if (q.kind) kindId.value = q.kind
  if (q.pay) payId.value = q.pay
  if (q.cols) gridCols.value = Number(q.cols)

  const pathParts = route.path.split('/').filter(Boolean)
  if (pathParts[1] === 'products' && pathParts[2]) {
    productType.value = pathParts[2]
  }
}


async function fetchProducts() {
  if (loading.value || !hasMore.value) {
    loading.value = false
    return
  }
  loading.value = true
  try {
    const orderMap = { newest: '1', price_asc: '3', price_desc: '2' }
    const dealLabels = activeFilter.deal.map(d => t(`market.${d}`)).filter(Boolean)
    const searchKey = (route.params.key || searchQuery.value || '').trim()
    const body = {
      page: page.value,
      adv_type: activeFilter.type,
      order: orderMap[sortBy.value] || '1',
      ...(searchKey ? { key: searchKey } : {}),
      ...(kindId.value ? { kind: kindId.value } : {}),
      ...(payId.value ? { pay: payId.value } : {}),
      ...(activeFilter.category ? { cat_id: String(activeFilter.category) } : {}),
      ...(dealLabels.length ? { deal: dealLabels } : {}),
      ...(activeFilter.provinces.length ? {
        ostan: activeFilter.provinces.join('،'),
      } : {}),
      ...(zaraFilter.value ? { zara: 'true' } : {}),
      ...(roostabadFilter.value ? { roostabad: 'true' } : {}),
      limit: 12,
    }

    const data = await $fetch('/market/api/market/products', {
      method: 'POST',
      body,
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
    })
    totalProducts.value = data.total
    if (data?.products?.length) {
      products.value.push(...data.products)
      perPage.value = data.products.length
      hasMore.value = data.hasMore ?? false
      page.value++
      if (data?.tags) {
        try {
          const res = await fetch(`/market/${data.tags}`).then((r)=>{
             if (r.status==200)
             {
              return r.text()

             }else{
                return ''
             }
            
            }).catch(()=>'')
          tags.value = res
        } catch (err) {
          console.error('خطا در دریافت فایل HTML تگ‌ها:', err)
          tags.value = ''
        }
      }
      if(activeFilter.category)
      {
        try {
          const res = await fetch(`/market/tags_txt/${String(activeFilter.category)}.html`).then((r)=>{
             if (r.status==200)
             {
              return r.text()

             }else{
                return ''
             }
            
            }).catch(()=>'')
          tags.value = res
        } catch (err) {
          tags.value = ''
          // console.error('خطا در دریافت فایل HTML تگ‌ها:', err)
        }
      }
    } else {
      hasMore.value = false
    }
  } catch {
    hasMore.value = false
  } finally {
    loading.value = false
  }
}

function onStripWheel(e) {
  const strip = e.target?.closest?.('.ostan-strip, .category-strip, .kind-strip')
  if (!strip) return
  if (strip.scrollWidth <= strip.clientWidth) return
  if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
    strip.scrollLeft += e.deltaY
    e.preventDefault()
  }
}

let observer = null
let revealObserver = null

watch(() => route.fullPath, () => {
  syncFiltersFromUrl()
  syncBrandFromRoute()
  page.value = 1
  products.value = []
  hasMore.value = true
  fetchProducts()
})

watch(filterOpen, async (open) => {
  if (!process.client || !isMobile.value) return
  document.body.style.overflow = open ? 'hidden' : ''
  if (open) {
    await nextTick()
    filterCloseBtn.value?.focus?.()
  }
})

// Modals should close on Escape, not only via the backdrop tap
const onFilterKeydown = (e) => {
  if (e.key === 'Escape' && filterOpen.value) filterOpen.value = false
}

onMounted(() => {
  syncFiltersFromUrl()
  syncBrandFromRoute()
  fetchProducts()
  startSlider()
  window.addEventListener('keydown', onFilterKeydown)

  const onToolbarScroll = () => {
    if (toolbarEl.value) {
      const currentScroll = window.scrollY
      if (currentScroll > lastScrollY && !isMobile.value && !filterOpen.value) {
        // Scrolling down on desktop - hide filter section
        filterHidden.value = true
      } else if (currentScroll < lastScrollY && filterHidden.value && !isMobile.value) {
        // Scrolling up on desktop - show filter section
        filterHidden.value = false
      }
      lastScrollY = currentScroll <= 0 ? 0 : currentScroll
    }
  }
  onToolbarScroll()
  toolbarScrollListener = onToolbarScroll

  toolbarEl.value?.addEventListener('wheel', onStripWheel, { passive: false })

  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting && !loading.value && hasMore.value) {
        fetchProducts()
      }
    },
    { rootMargin: '200px' }
  )
  if (scrollSentinel.value) observer.observe(scrollSentinel.value)

  if (process.client) {
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            revealObserver.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    )

    const observeReveals = () => {
      document.querySelectorAll('.reveal').forEach((el) => {
        if (el.closest('.market-page')) revealObserver.observe(el)
      })
    }
    observeReveals()

    const gridObserver = new MutationObserver(() => {
      observeReveals()
    })
    if (productsGrid.value) {
      gridObserver.observe(productsGrid.value, { childList: true, subtree: true })
    }
  }
})

onUnmounted(() => {
  stopSlider()
  window.removeEventListener('keydown', onFilterKeydown)
  if (toolbarScrollListener) window.removeEventListener('scroll', toolbarScrollListener)
  toolbarEl.value?.removeEventListener('wheel', onStripWheel)
  if (observer) observer.disconnect()
  if (revealObserver) revealObserver.disconnect()
})
</script>

<style lang="scss" scoped>
@use "sass:color";

.market-page {
  direction: rtl;
  background: $cream;
  min-height: 100vh;
  padding-bottom: 80px;
  padding-top: 50px;
  position: relative;
  color: $ink;
  font-family: 'IRANYekan', 'Vazirmatn', sans-serif;
  overflow-x: clip;
}


.grain {
  position: absolute;
  inset: -50%;
  opacity: 0.3;
  background-image:
    radial-gradient(circle at 20% 30%, rgba(200, 134, 26, 0.04) 0%, transparent 40%),
    url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E");
  mix-blend-mode: multiply;
}

.blob {
  position: absolute;
  width: 320px;
  height: 320px;
  opacity: 0.4;
  filter: blur(8px);

  &.blob-1 {
    color: rgba(232, 168, 56, 0.15);
    top: -60px;
    left: -100px;
    animation: blob-drift-1 22s ease-in-out infinite;
  }

  &.blob-2 {
    color: rgba(3, 160, 84, 0.12);
    bottom: 8%;
    right: -120px;
    width: 260px;
    height: 260px;
    animation: blob-drift-2 28s ease-in-out infinite;
  }
}

@keyframes blob-drift-1 {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  50%      { transform: translate(40px, 60px) rotate(40deg); }
}

@keyframes blob-drift-2 {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  50%      { transform: translate(-50px, -40px) rotate(-30deg); }
}

.hero-banner,
.market-toolbar,
.products-section {
  position: relative;
  z-index: 1;
}

.market-toolbar {
    position: sticky;
    top: 50px;
    z-index: 60;
    transition: box-shadow 0.3s ease, transform 0.3s ease;

    &.is-stuck {
      box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
    }

    &.is-hidden {
      transform: translateY(-100%);
    }

  .toolbar-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    background: rgba(255, 255, 255, 0.92);
    border-bottom: 1px solid rgba(226, 214, 189, 0.4);
    backdrop-filter: blur(8px);
    .toolbar-filter-toggle {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 18px;
      background: #fff;
      border: 1.5px solid rgba(226, 214, 189, 0.6);
      border-radius: $r-pill;
      font-size: 0.8rem;
      color: $ink-soft;
      cursor: pointer;
      font-family: inherit;
      transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);

      svg { transition: transform 0.25s ease; }

      &.active,
      &:hover {
        border-color: $leaf;
        color: $leaf;
        box-shadow: 0 4px 12px rgba(3, 160, 84, 0.1);

        svg { transform: rotate(90deg); }
      }

      .filter-count-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 18px;
        height: 18px;
        padding: 0 5px;
        border-radius: $r-pill;
        background: $berry;
        color: #fff;
        font-size: 0.68rem;
        font-weight: 700;
      }
    }
  }

  .toolbar-strips {
    background: rgba(255, 255, 255, 0.85);
  }
}

// Backdrop has to sit above the site header (1200/1300) and the mobile bottom
// nav (1000/2000) or the modal would slide in underneath them.
.filter-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2090;
  background: rgba(16, 26, 20, 0.45);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  animation: filter-fade 0.25s ease;
}

// Mobile filter modal: fills the viewport with a pinned header/footer and a
// scrollable body, so long lists (categories, deal types, 31 provinces) stay
// reachable without hunting for a sheet that runs off-screen.
.filter-drawer {
  position: fixed;
  inset: 0;
  z-index: 2100;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  padding-top: env(safe-area-inset-top);
  background: #fff;
  overflow: hidden;
  overscroll-behavior: contain;
  animation: filter-rise 0.3s cubic-bezier(0.22, 1, 0.36, 1);

  .drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(226, 214, 189, 0.45);
    background: #fff;
    flex-shrink: 0;

    .drawer-title {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;

      h4 {
        margin: 0;
        font-size: 1.05rem;
        font-weight: 800;
        color: $ink;
      }

      .drawer-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 20px;
        height: 20px;
        padding: 0 6px;
        border-radius: $r-pill;
        background: $leaf;
        color: #fff;
        font-size: 0.7rem;
        font-weight: 700;
      }
    }

    .drawer-close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;
      border: 1.5px solid rgba(226, 214, 189, 0.6);
      border-radius: 50%;
      background: #faf8f3;
      color: $ink-soft;
      font-size: 20px;
      line-height: 1;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        border-color: $berry;
        color: $berry;
      }
    }
  }

  .drawer-scroll {
    flex: 1;
    // overflow-x hidden keeps the full-bleed sticky group titles from adding a
    // horizontal scrollbar
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    padding: 0 16px 20px;
  }

  .drawer-group {
    padding: 16px 0 8px;

    & + & {
      border-top: 1px dashed rgba(226, 214, 189, 0.6);
    }

    .drawer-group-title {
      position: sticky;
      top: 0;
      z-index: 2;
      display: block;
      margin: 0 -16px 12px;
      padding: 12px 16px 8px;
      background: #fff;
      font-size: 0.76rem;
      font-weight: 800;
      color: $ink-soft;
    }

    .drawer-options {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .drawer-options-cats {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;

      .drawer-option-card {
        flex-direction: column;
        gap: 8px;
        padding: 12px 8px 10px;
        text-align: center;
        border-radius: 14px;
        background: #faf8f3;

        img {
          width: 30px;
          height: 30px;
          object-fit: contain;
        }
      }
    }

    .drawer-options-ostan {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
    }

    .drawer-option {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      min-height: 44px;
      padding: 10px 14px;
      background: #faf8f3;
      border: 1.5px solid transparent;
      border-radius: 12px;
      font-size: 0.85rem;
      font-weight: 500;
      color: $ink;
      font-family: inherit;
      cursor: pointer;
      transition: all 0.2s ease;

      svg {
        width: 12px;
        height: 12px;
        flex-shrink: 0;
      }

      &.active {
        background: rgba(3, 160, 84, 0.1);
        border-color: $leaf;
        color: $leaf;
        font-weight: 700;
      }

      &:active {
        transform: scale(0.97);
      }
    }
  }

  .drawer-footer {
    display: flex;
    gap: 10px;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    border-top: 1px solid rgba(226, 214, 189, 0.45);
    background: #fff;
    flex-shrink: 0;

    .drawer-clear {
      min-height: 48px;
      padding: 12px 18px;
      background: transparent;
      border: 1.5px solid rgba(226, 214, 189, 0.7);
      border-radius: 12px;
      color: $ink-soft;
      font-size: 0.85rem;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        border-color: $berry;
        color: $berry;
      }
    }

    .drawer-apply {
      flex: 1;
      min-height: 48px;
      padding: 12px;
      background: $leaf;
      border: none;
      border-radius: 12px;
      color: #fff;
      font-size: 0.9rem;
      font-weight: 800;
      font-family: inherit;
      cursor: pointer;
      box-shadow: $shadow-leaf;
      transition: all 0.2s ease;

      &:active {
        transform: scale(0.98);
      }
    }
  }
}

@keyframes filter-fade {
  from { opacity: 0; }
  to   { opacity: 1; }
}

@keyframes filter-rise {
  from { transform: translateY(24px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
}

.hero-banner {
  position: relative;
  overflow: hidden;

  .hero-slider {
    position: relative;
    height: 220px;

    .hero-slide {
      position: absolute; inset: 0;
      background-size: cover;
      background-position: center;
      display: flex;
      align-items: center;
      opacity: 0;
      transition: opacity 0.8s ease, transform 0.8s ease;
      transform: scale(1.05);

      &.active {
        opacity: 1;
        transform: scale(1);
        z-index: 1;
      }
    }

    .hero-overlay {
      position: absolute; inset: 0;
      background: linear-gradient(135deg, rgba(3, 160, 84, 0.88) 0%, rgba(2, 138, 70, 0.7) 50%, rgba(232, 168, 56, 0.25) 100%);
    }

    .hero-content {
      position: relative; z-index: 2;
      padding: 24px 28px;
      max-width: 480px;

      .hero-title {
        color: #fff;
        font-size: 1.35rem;
        font-weight: 800;
        margin-bottom: 6px;
        line-height: 1.3;
        text-shadow: 0 2px 8px rgba(0,0,0,0.15);
      }

      .hero-desc {
        color: rgba(255,255,255,0.88);
        font-size: 0.85rem;
        margin-bottom: 16px;
        line-height: 1.6;
      }

      .hero-cta {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 10px 22px;
        background: $sun;
        color: $ink;
        border: none;
        border-radius: $r-pill;
        font-size: 0.85rem;
        font-weight: 700;
        font-family: inherit;
        cursor: pointer;
        transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        box-shadow: 0 4px 14px rgba(232, 168, 56, 0.35);

        svg { transition: transform 0.25s ease; }

        &:hover {
          background: color.adjust($sun, $lightness: -6%);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(232, 168, 56, 0.45);
          svg { transform: translateX(3px); }
        }

        &:active { transform: translateY(0) scale(0.97); }
      }
    }
  }

  .slider-dots {
    position: absolute;
    bottom: 12px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 3;
    display: flex;
    gap: 8px;

    .slider-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: rgba(255,255,255,0.35);
      border: none;
      cursor: pointer;
      padding: 0;
      transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);

      &.active {
        background: $sun;
        width: 24px;
        border-radius: 4px;
      }

      &:hover { background: rgba(255,255,255,0.7); }
    }
  }
}

.strip-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}

.category-strip {
  background: rgba(255,255,255,0.85);
  backdrop-filter: blur(12px);
  padding: 14px 0;
  border-bottom: 1px solid rgba(226, 214, 189, 0.4);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    width: 6px;
    @media (max-width: 768px) {
      display: block;
    }
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    @media (max-width: 768px) {
      height: 100%;
    }
  }

  .strip-container {
    display: flex;
    gap: 6px;
    padding: 0 12px;
  }

  .cat-pill {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: none;
    border: 1.5px solid transparent;
    border-radius: $r-lg;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    flex-shrink: 0;
    font-family: inherit;

    &.active {
      background: rgba(232, 168, 56, 0.1);
      border-color: rgba(232, 168, 56, 0.3);

      .cat-icon { background: $leaf; }
      .cat-icon img { filter: brightness(0) invert(1); }
      .cat-name { color: $leaf; font-weight: 700; }
    }

    .cat-icon {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: $cream-deep;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 10px;
      transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        transition: all 0.3s ease;
      }
    }

    &:hover {
      .cat-icon {
        background: $leaf;
        img { filter: brightness(0) invert(1); transform: scale(1.1); }
      }
      .cat-name { color: $leaf; }
    }

    .cat-name {
      font-size: 0.7rem;
      color: $ink-soft;
      white-space: nowrap;
      transition: color 0.3s ease;
    }
  }
}

.kind-strip {
  background: rgba(255,255,255,0.6);
  backdrop-filter: blur(8px);
  padding: 10px 0;
  border-bottom: 1px solid rgba(226, 214, 189, 0.3);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar { display: none; }

  .strip-container {
    display: flex;
    gap: 6px;
    padding: 0 16px;
  }

  .strip-divider {
    flex-shrink: 0;
    width: 1px;
    background: rgba(226, 214, 189, 0.5);
    margin: 0 4px;
  }

  .kind-pill {
    flex-shrink: 0;
    padding: 7px 18px;
    background: #fff;
    border: 1.5px solid rgba(226, 214, 189, 0.5);
    border-radius: $r-pill;
    cursor: pointer;
    font-size: 0.78rem;
    color: $ink-soft;
    font-family: inherit;
    white-space: nowrap;
    transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);

    &.active {
      background: $leaf;
      border-color: $leaf;
      color: #fff;
      font-weight: 600;
      box-shadow: $shadow-leaf;
    }

    &:hover:not(.active) {
      border-color: $leaf;
      color: $leaf;
    }
  }
}

.ostan-strip {
  background: rgba(247, 243, 232, 0.92);
  backdrop-filter: blur(8px);
  padding: 12px 0;
  border-bottom: 1px solid rgba(226, 214, 189, 0.35);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 768px) {
    scrollbar-width: thin;
    scrollbar-color: rgba(3, 160, 84, 0.4) rgba(226, 214, 189, 0.3);

    &::-webkit-scrollbar {
      height: 6px;
      display: block;
    }
    &::-webkit-scrollbar-track {
      background: rgba(226, 214, 189, 0.3);
      border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(3, 160, 84, 0.4);
      border-radius: 3px;
    }
  }

  &::-webkit-scrollbar { display: none; }

  .strip-container {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 16px;
    max-width: none;
    width: max-content;
  }

  .ostan-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px;
    margin-right: 4px;
    font-size: 0.72rem;
    font-weight: 800;
    color: $ink;
    background: linear-gradient(135deg, rgba(3, 160, 84, 0.14) 0%, rgba(232, 168, 56, 0.14) 100%);
    border: 1px solid rgba(3, 160, 84, 0.18);
    border-radius: $r-pill;
    white-space: nowrap;
    flex-shrink: 0;

    svg { color: $leaf; }
  }

  .ostan-pill {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 7px 14px;
    background: #fff;
    border: 1.5px solid rgba(226, 214, 189, 0.5);
    border-radius: $r-pill;
    cursor: pointer;
    font-size: 0.75rem;
    color: $ink-soft;
    font-family: inherit;
    white-space: nowrap;
    transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);

    .ostan-marker {
      width: 11px;
      height: 11px;
      color: $ink-soft;
      transition: color 0.25s ease;
    }

    &.active {
      background: $leaf;
      border-color: $leaf;
      color: #fff;
      font-weight: 600;
      box-shadow: $shadow-leaf;

      .ostan-marker { color: $sun; }
    }

    &:hover:not(.active) {
      border-color: $leaf;
      color: $leaf;

      .ostan-marker { color: $leaf; }
    }
  }
}

.active-filters-bar {
  background: rgba(255,255,255,0.9);

  padding: 10px 0;
  border-bottom: 1px solid rgba(226, 214, 189, 0.3);
  width: 100%;

  .filter-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 12px;
    background: rgba(3, 160, 84, 0.08);
    color: $leaf;
    border-radius: $r-pill;
    font-size: 0.75rem;
    font-weight: 500;
    border: 1px solid rgba(3, 160, 84, 0.15);

    svg { flex-shrink: 0; }

    .chip-remove {
      background: none;
      border: none;
      color: inherit;
      cursor: pointer;
      font-size: 15px;
      line-height: 1;
      padding: 0 2px;
      opacity: 0.6;
      transition: opacity 0.2s;

      &:hover { opacity: 1; }
    }
  }

  .chip-clear {
    background: none;
    border: none;
    color: $ink-soft;
    font-size: 0.75rem;
    cursor: pointer;
    padding: 4px 8px;
    font-family: inherit;
    transition: color 0.2s;

    &:hover { color: $berry; }
  }
}

.products-section {
  padding: 24px 0;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;

    .header-left {
      display: flex;
      align-items: baseline;
      gap: 10px;

      .section-title {
        font-size: 1.15rem;
        font-weight: 800;
        color: $ink;
        margin: 0;
      }

      .product-count {
        font-size: 0.8rem;
        color: $ink-soft;
      }
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 10px;
    }
  }

  .header-search {
    position: relative;
    display: flex;
    align-items: center;

    svg {
      position: absolute;
      right: 12px;
      color: $ink-soft;
      pointer-events: none;
    }

    input {
      width: 200px;
      padding: 9px 32px 9px 30px;
      border: 1.5px solid rgba(226, 214, 189, 0.6);
      border-radius: $r-pill;
      font-size: 0.8rem;
      color: $ink;
      background: #fff;
      outline: none;
      font-family: inherit;
      transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);

      &::placeholder {
        color: $ink-soft;
      }

      &:focus {
        border-color: $leaf;
        box-shadow: 0 0 0 3px rgba(3, 160, 84, 0.1);
      }
    }

    .search-clear {
      position: absolute;
      left: 10px;
      background: none;
      border: none;
      color: $ink-soft;
      font-size: 16px;
      line-height: 1;
      cursor: pointer;
      padding: 0 3px;
      transition: color 0.2s;

      &:hover { color: $berry; }
    }

    @media (max-width: 640px) {
      input { width: 130px; }
    }
  }

  .grid-toggle-group {
    display: flex;
    gap: 3px;
    margin-left: 6px;
  }

  .grid-toggle-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: #fff;
    border: 1.5px solid rgba(226, 214, 189, 0.6);
    border-radius: $r-sm;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);

    svg { color: $ink-soft; }

    &.active {
      background: $leaf;
      border-color: $leaf;

      svg { color: #fff; }
    }

    &:hover:not(.active) {
      border-color: $leaf;
      svg { color: $leaf; }
    }
  }

  .filter-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 9px 16px;
    background: #fff;
    border: 1.5px solid rgba(226, 214, 189, 0.6);
    border-radius: $r-pill;
    font-size: 0.8rem;
    color: $ink-soft;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);

    svg { transition: transform 0.25s ease; }

    &:hover {
      border-color: $leaf;
      color: $leaf;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(3, 160, 84, 0.1);

      svg { transform: rotate(90deg); }
    }
  }

  .sort-wrapper {
    position: relative;
    display: flex;
    align-items: center;

    .sort-icon {
      position: absolute;
      right: 12px;
      color: $ink-soft;
      pointer-events: none;
    }

    .sort-select {
      padding: 9px 36px 9px 14px;
      border: 1.5px solid rgba(226, 214, 189, 0.6);
      border-radius: $r-pill;
      font-size: 0.8rem;
      color: $ink-soft;
      background: #fff;
      cursor: pointer;
      outline: none;
      font-family: inherit;
      appearance: none;
      -webkit-appearance: none;
      transition: border-color 0.25s ease;

      &:focus { border-color: $leaf; }
    }
  }
}

.loading-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 14px;

  .sk-card {
    background: #fff;
    border-radius: $r-lg;
    overflow: hidden;
    box-shadow: $shadow-warm-sm;

    .sk-img {
      width: 100%;
      padding-bottom: 75%;
      background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
    }

    .sk-body {
      padding: 12px 14px 14px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .sk-line {
      height: 12px;
      border-radius: 4px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;

      &.short { width: 65%; }
      &.tiny { width: 40%; height: 10px; }
    }
  }
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;

  .empty-orb {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: rgba(3, 160, 84, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    color: $leaf;
    animation: orb-pulse 2s ease infinite;
  }

  h4 {
    font-size: 1.1rem;
    color: $ink;
    margin-bottom: 8px;
  }

  p {
    font-size: 0.85rem;
    color: $ink-soft;
    max-width: 300px;
    margin: 0 auto 24px;
    line-height: 1.6;
  }

  .reset-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 11px 24px;
    background: $leaf;
    color: #fff;
    border: none;
    border-radius: $r-pill;
    font-size: 0.85rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    box-shadow: $shadow-leaf;
  }
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(var(--grid-cols, 4), 1fr);
  gap: 12px;

  .product-card {
    background: #fff;
    border-radius: $r-lg;
    overflow: hidden;
    box-shadow: $shadow-warm-sm;
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: $shadow-warm-md;
    }

    .card-inner {
      padding: 12px;
      position: relative;

      .badge-lists {
        position: absolute;
        top: 20px;
        right: 20px;
        z-index: 2;
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;
        gap: 5px;
        max-width: calc(100% - 24px);
        pointer-events: none;
      }

      .badge {
        position: relative;
        display: inline-flex;
        align-items: center;
        padding: 4px 8px;
        border-radius: $r-sm;
        font-size: 0.7rem;
        font-weight: 600;
        white-space: nowrap;

        &.badge-success { background: rgba(3, 160, 84, 0.6); color: #fff; border: 1px solid rgba(3, 160, 84, 0.3); }
        &.badge-warning { background: rgba(232, 168, 56, 0.1); color: $sun; border: 1px solid rgba(232, 168, 56, 0.3); }
        &.badge-danger { background: rgba(220, 38, 38, 0.8); color: #fff; border: 1px solid rgba(220, 38, 38, 0.3); }
        &.badge-gold { background: #ca8a04; color: #fff; }
        &.badge-blue { background: #0432ca; color: #fff; }

        &.badge-fresh {
          background: linear-gradient(135deg, rgba(94 13 148), rgba(3, 160, 84, 1));
          color: #fff;
          border: 1px solid rgba(13, 148, 136, 0.35);
        }
      }

      .product-thumbnail {
        display: block;
        margin-bottom: 12px;
        border-radius: $r-md;
        overflow: hidden;
        background: #f9f9f9;

        img { width: 100%; height: 190px; object-fit: cover; transition: transform 0.3s ease; }

        &:hover img { transform: scale(1.05); }
      }

      .product-title {
        display: block;
        font-size: 0.85rem;
        font-weight: 600;
        color: $ink;
        margin-bottom: 6px;
        line-height: 1.4;
        height: 2.8em;
        overflow: hidden;
        text-decoration: none;

        &:hover { color: $leaf; }
      }

      .seller-info {
        font-size: 0.75rem;
        color: $ink-soft;
        margin-bottom: 12px;
      }

      .phone-btn {

        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        background: #03a054;
        border-radius: 50%;
        color: #fff;
        text-decoration: none;
        transition: all 0.3s ease;
        position: absolute;
        bottom: 10px;
        left: 10px;
        &:hover { background: color.adjust($leaf, $lightness: -10%); transform: scale(1.1); }

        i { font-size: 0.9rem; }
      }
    }
  }

  .cta-card {
    background: linear-gradient(135deg, #fef3c7, #fed7aa);
    border-radius: $r-lg;
    padding: 20px;
    position: relative;
    overflow: hidden;
    height: 100%;
    display: flex;
    flex-direction: column;

    &.cta-style-1 { background: linear-gradient(135deg, #dcfce7, #bbf7d0); }
    &.cta-style-2 { background: linear-gradient(135deg, #fed7aa, #fed7aa); }

    .cta-atmosphere {
      position: absolute;
      top: 0;
      right: 0;
      width: 80px;
      height: 80px;
      opacity: 0.1;

      .cta-leaf {
        width: 100%;
        height: 100%;
        transform: rotate(45deg);
      }
    }

    .cta-body {
      position: relative;
      z-index: 2;

      .cta-label {
        font-size: 0.95rem;
        font-weight: 700;
        color: $ink;
        margin-bottom: 8px;
        display: block;
      }

      .cta-desc {
        font-size: 0.75rem;
        color: $ink-soft;
        margin-bottom: 16px;
        line-height: 1.6;
        margin-top: 20px;
        min-height: 120px;
      }

      .cta-action {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 8px 16px;
        background: $leaf;
        color: #fff;
        border: none;
        border-radius: $r-pill;
        font-size: 0.75rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          background: color.adjust($leaf, $lightness: -10%);
          transform: translateY(-2px);
          box-shadow: $shadow-leaf;
        }

        &:active { transform: translateY(0) scale(0.97); }
      }
    }
  }
}

.scroll-sentinel {
  height: 1px;
  margin-top: 20px;

  .sentinel-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    background: rgba(255,255,255,0.5);
    border-radius: $r-lg;
    font-size: 0.85rem;
    color: $ink-soft;

    .sentinel-spinner {
      width: 20px;
      height: 20px;

      svg {
        animation: spin 2s linear infinite;
        circle {
          stroke: $leaf;
          stroke-linecap: round;
        }
      }
    }
  }

  .sentinel-end {
    opacity: 0.7;
  }
}

.tags-seo-container {
  margin: 40px 0;
  padding: 0 16px;

  .tag-seo-section {
    background: #fff;
    border-radius: $r-lg;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: $shadow-warm-sm;

    .seo-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 40px;
      color: $ink-soft;

      .loading-spinner {
        width: 24px;
        height: 24px;
        border: 2px solid rgba(3, 160, 84, 0.2);
        border-top: 2px solid $leaf;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
    }
  }

  .seo-content {
    line-height: 1.8;
    text-align: justify;
    color: $ink;

    p { margin-bottom: 16px; }
    h3 { font-size: 1.2rem; font-weight: 700; color: $leaf; margin: 24px 0 16px; }
    a { color: $leaf; text-decoration: none; font-weight: 600; }
    a:hover { text-decoration: underline; }
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes orb-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}
</style>